import type { QuoteDocument } from 'paintscout'
import { contentHash } from './content-hash'

/**
 * Version of the rules that wrote `quote.translations`. A quote without it was translated before
 * ENG-2416 (#24890), when the model often returned area names such as "Front Side" unchanged and
 * that English copy was stored as the translation. Those copies are never sent again, because
 * every reader treats a stored value as translated — and an edit keeps the same hash, a duplicate
 * copies the map. Writers stamp this version once they have dropped those copies.
 *
 * Echoes stored under this version came from the current prompt and are trusted, so text the model
 * genuinely leaves unchanged (brand names, "hr") is not re-sent on every save or download.
 */
export const QUOTE_TRANSLATIONS_VERSION = 2

export function hasLegacyTranslations(quote: Pick<QuoteDocument, 'translationsVersion'> | null | undefined): boolean {
  return quote?.translationsVersion !== QUOTE_TRANSLATIONS_VERSION
}

/** True when `translated` is a pre-version echo of `source` that must be treated as missing. */
export function isLegacyTranslationEcho(
  quote: Pick<QuoteDocument, 'translationsVersion'> | null | undefined,
  source: string,
  translated: string | null | undefined
): boolean {
  return translated === source && hasLegacyTranslations(quote)
}

/**
 * Returns `quote.translations` with every legacy echo of a current string removed, across all
 * languages. Returns the stored map unchanged for a versioned quote. Entries for strings that are
 * no longer on the quote are left for the callers' own pruning.
 */
export function withoutLegacyTranslationEchoes(
  quote: Pick<QuoteDocument, 'translations' | 'translationsVersion'>,
  strings: Iterable<string>
): Record<string, Record<string, string>> {
  const translations = quote.translations ?? {}
  if (!hasLegacyTranslations(quote)) return translations

  const sourceByHash = new Map<string, string>()
  for (const s of strings) sourceByHash.set(contentHash(s), s)

  return Object.fromEntries(
    Object.entries(translations).map(([lang, map]) => [
      lang,
      Object.fromEntries(Object.entries(map ?? {}).filter(([hash, value]) => value !== sourceByHash.get(hash)))
    ])
  )
}
